// Layer 2 — post-parse plausibility validation.
//
// This module OBSERVES a completed parse and reports plausibility concerns.
// It never changes any parsing decision (column pick, tie-break, sheet
// selection, header detection, or conversion arithmetic). Every threshold
// below is a named constant so it can be re-tuned once shadow-mode telemetry
// tells us what a real-world false-positive rate looks like.

import type {
  ParsedDataPoint,
  ColumnProfile,
  DatasetShape,
  ColumnAssignment,
} from './types';
import { detectSuspectWindows } from './suspectPeaks';
import type { SuspectCheck, SuspectWindow, TimeRange } from './suspectPeaks';

// ─── Tunable thresholds (pending shadow-mode telemetry calibration) ──────────
const LOW_PEAK_KW = 5.0;              // peak below this is suspiciously low for a residence
const HIGH_PEAK_KW = 60;             // peak above this is high for residential service
const RIVAL_CONFIDENCE_WINDOW = 0.15; // rival demand column within this confidence of the pick
const ROW_COUNT_COVERAGE_FLOOR = 0.70; // flag when actual rows < this fraction of expected
const DEGENERATE_MODE_FRACTION = 0.30; // one repeated nonzero value dominates this share of readings
const PARTIAL_YEAR_MIN_DAYS = 30;     // below this the existing <30-day warning already fires
const PARTIAL_YEAR_MAX_DAYS = 364;    // 365+ days is treated as a full year
const RIVAL_SHEET_SCORE_RATIO = 0.70; // rival tab scoring this fraction of the chosen tab is competitive
const RIVAL_SHEET_MIN_ROWS = 100;     // ...and holding at least this many rows

export type PlausibilitySeverity = 'verify' | 'info';

export interface PlausibilityFlag {
  check: string;
  severity: PlausibilitySeverity;
  message: string;
  detail: Record<string, unknown>;
}

export interface PlausibilityInput {
  dataPoints: ParsedDataPoint[];
  intervalMinutes: number | null;
  profiles: ColumnProfile[];
  shape: DatasetShape;
  assignment: ColumnAssignment;
  sheetCount: number;
  /** Runs of readings the source marks as estimated (utility_estimated). */
  estimatedRuns?: TimeRange[];
  /** Deliverable kW of the service. Absent at parse time, so
   *  exceeds_service_capacity only fires where the project is known. */
  serviceKw?: number | null;
}

/**
 * The one sentence a suspect-peak flag may say before the project is unlocked:
 * the measured peak is paid content (ux.md LOCKED #1), so no kilowatt value,
 * no time of the peak and no before-and-after. The window, its evidence and
 * the numbers ride in `detail` and render only on the unlocked cards.
 */
export const SUSPECT_PEAK_MESSAGE =
  "A possible meter data error affects this building's peak. Review after unlocking.";

const SUSPECT_CHECK_ORDER: SuspectCheck[] = [
  'gap_catchup',
  'exceeds_service_capacity',
  'daily_energy_spike',
  'utility_estimated',
];

/** One verify flag per suspect check that fired, carrying its windows. */
export function suspectPeakFlags(windows: SuspectWindow[]): PlausibilityFlag[] {
  return SUSPECT_CHECK_ORDER.flatMap(check => {
    const mine = windows.filter(w => w.check === check);
    if (mine.length === 0) return [];
    return [{
      check,
      severity: 'verify' as const,
      message: SUSPECT_PEAK_MESSAGE,
      detail: {
        windows: mine.map(w => ({
          start: w.start, end: w.end, hours: w.hours, peakKw: w.peakKw, elsewhereKw: w.elsewhereKw, ...w.detail,
        })),
      },
    }];
  });
}

function spanDaysOf(dataPoints: ParsedDataPoint[]): number | null {
  if (dataPoints.length < 2) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const p of dataPoints) {
    const t = new Date(p.timestamp).getTime();
    if (!Number.isFinite(t)) continue;
    if (t < min) min = t;
    if (t > max) max = t;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return (max - min) / (1000 * 60 * 60 * 24);
}

function columnLabel(profile: ColumnProfile | undefined, index: number): string {
  return profile?.header?.trim() || `column ${index + 1}`;
}

export function validatePlausibility(input: PlausibilityInput): PlausibilityFlag[] {
  const { dataPoints, intervalMinutes, profiles, shape, assignment, sheetCount } = input;
  const flags: PlausibilityFlag[] = [];

  const kws = dataPoints.map(p => p.kw);
  const hasData = kws.length > 0;
  const peakKw = hasData ? Math.max(...kws) : 0;
  const demandProfile = profiles.find(p => p.index === assignment.demandIndex);
  const chosenDemandLabel = columnLabel(demandProfile, assignment.demandIndex);
  const spanDays = spanDaysOf(dataPoints);

  // 1. low_peak — peak demand implausibly low for a residence.
  if (hasData && peakKw < LOW_PEAK_KW) {
    flags.push({
      check: 'low_peak',
      severity: 'verify',
      message:
        'This peak demand is unusually low for a residence. Please confirm the highlighted column contains demand data.',
      detail: { peakKw, threshold: LOW_PEAK_KW, demandColumn: chosenDemandLabel },
    });
  }

  // 2. high_peak — peak demand high for a single-family residential service.
  if (hasData && peakKw > HIGH_PEAK_KW) {
    flags.push({
      check: 'high_peak',
      severity: 'verify',
      // The threshold is stated, never the peak: this banner shows before the
      // project is unlocked, and the measured peak is paid content. The value
      // stays in `detail` for telemetry.
      message:
        `This peak demand is above ${HIGH_PEAK_KW} kW, which is unusually high for a residential service. ` +
        'Please confirm this is a single-family residential meter file.',
      detail: { peakKw, threshold: HIGH_PEAK_KW, demandColumn: chosenDemandLabel },
    });
  }

  // 3. rival_demand_candidates — another column was nearly as good a demand pick.
  const chosenConfidence = demandProfile?.confidence ?? 0;
  const rivals = (assignment.rivalCandidates ?? []).filter(
    r => Math.abs(r.confidence - chosenConfidence) <= RIVAL_CONFIDENCE_WINDOW
  );
  if (rivals.length >= 1) {
    const rivalLabels = rivals.map(r => r.header?.trim() || `column ${r.index + 1}`);
    flags.push({
      check: 'rival_demand_candidates',
      severity: 'verify',
      message:
        `More than one column in this file could be demand data. We used ${chosenDemandLabel}. ` +
        'Please confirm this is correct.',
      detail: {
        chosen: chosenDemandLabel,
        chosenConfidence,
        rivals: rivals.map(r => ({
          column: r.header?.trim() || `column ${r.index + 1}`,
          kind: r.kind,
          confidence: r.confidence,
        })),
        rivalColumns: rivalLabels,
        window: RIVAL_CONFIDENCE_WINDOW,
      },
    });
  }

  // 4. weak_header — the demand column was chosen purely from the shape of the
  //    data, with NO keyword-tier header match at all. We deliberately do NOT
  //    fire on low header-confidence alone: a keyword-matched column (e.g. one
  //    literally named "kW", demandTier 1) is labeled by definition, so the
  //    "chosen from the shape of the data" message would be flatly inaccurate
  //    for it — and flagging labeled columns trains users to ignore the banner.
  //    Fire only when the pick rests on pure data-shape inference.
  const headerConfident = shape.headerConfident !== false; // retained for telemetry detail
  // A kWh label counts as a label. demandTier only matches kW-family patterns, so
  // a column named `interval_kWh` scored 0 and got told it was "chosen from the
  // shape of the data" — flatly untrue, and the exact miscalibration the comment
  // above warns against. These flags are load-bearing now (high_peak is what
  // stands between a meter-rollover artifact and a permit application), so a flag
  // that cries wolf on a labeled column is a real cost.
  const demandHasKeyword =
    (demandProfile?.headerSignal.demandTier ?? 0) > 0 ||
    (demandProfile?.headerSignal.isKwh ?? false) ||
    (demandProfile?.headerSignal.isKva ?? false);
  if (!demandHasKeyword) {
    flags.push({
      check: 'weak_header',
      severity: 'verify',
      message:
        'TrueLoad could not confidently identify a labeled header for the demand column — it was chosen from the ' +
        'shape of the data. Please confirm the highlighted column contains demand data.',
      detail: {
        headerConfident,
        demandColumn: chosenDemandLabel,
        demandHeaderTier: demandProfile?.headerSignal.demandTier ?? 0,
      },
    });
  }

  // 5. row_count_shortfall — far fewer valid rows than the span+interval imply.
  if (intervalMinutes && intervalMinutes > 0 && spanDays && spanDays > 0 && hasData) {
    const expected = Math.round(spanDays * (1440 / intervalMinutes));
    if (expected > 0) {
      const coverage = kws.length / expected;
      if (coverage < ROW_COUNT_COVERAGE_FLOOR) {
        flags.push({
          check: 'row_count_shortfall',
          severity: 'verify',
          message:
            'This file has fewer readings than expected for its date range. Some interval data may be missing, ' +
            'which can hide the true peak. Please confirm the export is complete.',
          detail: {
            actual: kws.length,
            expected,
            coveragePercent: Math.round(coverage * 1000) / 10,
            intervalMinutes,
            spanDays: Math.round(spanDays * 10) / 10,
          },
        });
      }
    }
  }

  // 6. partial_year — 30–364 days. (The <30-day warning is emitted elsewhere.)
  if (spanDays !== null && spanDays >= PARTIAL_YEAR_MIN_DAYS && spanDays <= PARTIAL_YEAR_MAX_DAYS) {
    flags.push({
      check: 'partial_year',
      severity: 'info',
      message:
        `This dataset covers about ${Math.round(spanDays)} days. NEC 220.87 Condition 1 is based on a full year of ` +
        'data; a shorter window may miss the seasonal peak (e.g. winter heating or summer cooling).',
      detail: { spanDays: Math.round(spanDays * 10) / 10 },
    });
  }

  // 7. degenerate_demand — all-zero, constant, or a single nonzero value dominates.
  if (kws.length >= 2) {
    const allZero = kws.every(v => v === 0);
    const constant = kws.every(v => v === kws[0]);
    let repeatedValue: number | null = null;
    let repeatedFraction = 0;
    if (!allZero && !constant) {
      const nonzero = kws.filter(v => v !== 0); // exclude baseline/vacancy zeros
      if (nonzero.length > 0) {
        const counts = new Map<number, number>();
        for (const v of nonzero) counts.set(v, (counts.get(v) ?? 0) + 1);
        let modeCount = 0;
        for (const [v, c] of counts) {
          if (c > modeCount) { modeCount = c; repeatedValue = v; }
        }
        repeatedFraction = modeCount / nonzero.length;
      }
    }
    if (allZero || constant || repeatedFraction > DEGENERATE_MODE_FRACTION) {
      const kind = allZero ? 'all_zero' : constant ? 'constant' : 'dominant_repeat';
      flags.push({
        check: 'degenerate_demand',
        severity: 'verify',
        message:
          'The selected demand column does not vary the way real interval demand normally does. ' +
          'Please confirm the highlighted column contains demand data.',
        detail: {
          kind,
          demandColumn: chosenDemandLabel,
          repeatedValue,
          repeatedFraction: Math.round(repeatedFraction * 1000) / 10,
        },
      });
    }
  }

  // 8. rival_sheets — a non-chosen tab in a multi-sheet workbook looks competitive.
  if (sheetCount > 1 && shape.sheetScores && shape.sheetScores.length > 1) {
    const chosenEntry = shape.sheetScores.find(s => s.name === shape.sheet);
    const chosenScore = chosenEntry?.score ?? 0;
    if (chosenScore > 0) {
      const competitors = shape.sheetScores.filter(
        s =>
          s.name !== shape.sheet &&
          s.score >= RIVAL_SHEET_SCORE_RATIO * chosenScore &&
          s.rowCount >= RIVAL_SHEET_MIN_ROWS
      );
      if (competitors.length > 0) {
        flags.push({
          check: 'rival_sheets',
          severity: 'verify',
          message:
            `This file contains multiple data tabs. We used '${shape.sheet}'. ` +
            'Please confirm this tab contains the interval data.',
          detail: {
            chosenSheet: shape.sheet,
            chosenScore,
            competitors: competitors.map(c => ({ name: c.name, score: c.score, rowCount: c.rowCount })),
            competitorNames: competitors.map(c => c.name),
          },
        });
      }
    }
  }

  // 9-12. Suspect peaks — gap_catchup, exceeds_service_capacity,
  //       daily_energy_spike, utility_estimated (suspectPeaks.ts). Evidence of
  //       a data error, never a judgement that a value is high.
  flags.push(...suspectPeakFlags(detectSuspectWindows({
    points: dataPoints,
    intervalMinutes,
    serviceKw: input.serviceKw ?? null,
    estimatedRuns: input.estimatedRuns ?? null,
  })));

  return flags;
}
